<template>
  <v-row>
    <v-col cols="12" class="pb-0">
      <div class="toolbar d-flex align-center flex-wrap ga-1 px-2 py-1 border rounded">
        <v-tooltip
          v-for="toggle in toggles"
          :key="toggle.name"
          :text="toggle.name"
          location="bottom"
        >
          <template #activator="{ props: tip }">
            <v-btn
              v-bind="tip"
              :icon="toggle.icon"
              size="small"
              rounded="lg"
              :variant="toggle.model.value ? 'flat' : 'text'"
              :color="toggle.model.value ? 'primary' : undefined"
              :aria-pressed="toggle.model.value"
              :aria-label="toggle.name"
              @click="toggle.model.value = !toggle.model.value"
            />
          </template>
        </v-tooltip>

        <v-divider vertical class="mx-2 my-1" />

        <v-menu
          v-model="problemsOpen"
          :close-on-content-click="false"
          location="bottom start"
        >
          <template #activator="{ props: menu }">
            <v-tooltip :disabled="problemsOpen" text="Problemas" location="bottom">
              <template #activator="{ props: tip }">
                <v-btn
                  v-bind="mergeProps(menu, tip)"
                  size="small"
                  rounded="lg"
                  variant="text"
                  icon
                  aria-label="Problemas"
                >
                  <v-badge
                    :model-value="errors.length + warnings.length > 0"
                    :content="errors.length + warnings.length"
                    :color="errors.length > 0 ? 'error' : 'warning'"
                  >
                    <v-icon icon="fa fa-triangle-exclamation" />
                  </v-badge>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <v-card width="440">
            <v-card-item>
              <v-card-title class="text-subtitle-1">
                Problemas
              </v-card-title>
              <v-card-subtitle>
                {{ errors.length }} erro(s) · {{ warnings.length }} aviso(s). Erros impedem salvar, avisos não.
              </v-card-subtitle>
            </v-card-item>
            <v-divider />
            <v-list v-if="sortedIssues.length" density="compact" max-height="420">
              <v-list-item
                v-for="(issue, i) in sortedIssues"
                :key="i"
                :link="!!issue.key"
                @click="issue.key && onPick(issue.key)"
              >
                <template #prepend>
                  <v-icon size="small" :color="issue.level" :icon="levelIcon[issue.level]" />
                </template>
                <v-list-item-title class="text-wrap text-body-2">
                  {{ issue.text }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
            <v-card-text v-else class="text-medium-emphasis">
              Nenhum problema.
            </v-card-text>
          </v-card>
        </v-menu>

        <v-menu
          v-model="historyOpen"
          :close-on-content-click="false"
          location="bottom start"
        >
          <template #activator="{ props: menu }">
            <v-tooltip :disabled="historyOpen" text="Histórico de alterações" location="bottom">
              <template #activator="{ props: tip }">
                <v-btn
                  v-bind="mergeProps(menu, tip)"
                  icon="fa fa-clock-rotate-left"
                  size="small"
                  rounded="lg"
                  variant="text"
                  aria-label="Histórico de alterações"
                />
              </template>
            </v-tooltip>
          </template>
          <v-card width="480">
            <v-card-item>
              <v-card-title class="text-subtitle-1">
                Histórico de alterações
              </v-card-title>
              <v-card-subtitle>Mais recentes primeiro. Nada é salvo até clicar em Salvar.</v-card-subtitle>
            </v-card-item>
            <v-divider />
            <v-list v-if="history.length" density="compact" max-height="420">
              <v-list-item v-for="(entry, i) in history" :key="i">
                <v-list-item-title class="text-wrap mono-wrap">
                  {{ entry }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
            <v-card-text v-else class="text-medium-emphasis">
              Nenhuma alteração ainda.
            </v-card-text>
          </v-card>
        </v-menu>

        <v-divider vertical class="mx-2 my-1" />

        <v-tooltip
          v-for="edit in edits"
          :key="edit.icon"
          :text="edit.blocker ?? edit.name"
          location="bottom"
        >
          <template #activator="{ props: tip }">
            <span v-bind="tip">
              <v-btn
                :icon="edit.icon"
                size="small"
                rounded="lg"
                variant="text"
                :disabled="disabled || edit.blocker !== null"
                :aria-label="edit.name"
                @click="edit.run"
              />
            </span>
          </template>
        </v-tooltip>

        <v-divider vertical class="mx-2 my-1" />

        <v-tooltip text="Baixar SVG" location="bottom">
          <template #activator="{ props: tip }">
            <v-btn
              v-bind="tip"
              icon="fa fa-download"
              size="small"
              rounded="lg"
              variant="text"
              aria-label="Baixar SVG"
              @click="download"
            />
          </template>
        </v-tooltip>

        <v-spacer />
        <span class="text-caption text-medium-emphasis pe-2">
          <template v-if="selectedKeys.length > 1">{{ selectedKeys.length }} selecionadas · </template>
          {{ rows.length }} de {{ linking.shapes.value.length }} formas
        </span>
      </div>
    </v-col>
    <v-col cols="12" md="5">
      <div class="layers border rounded">
        <div
          v-for="shape in rows"
          :id="`layer-${shape.key}`"
          :key="shape.key"
          class="layer d-flex align-center ga-2 px-2"
          :class="{ 'layer--selected': selectedKeys.includes(shape.key), 'layer--hover': shape.key === hoverKey }"
          :style="{ paddingLeft: `${8 + shape.depth * 18}px !important` }"
          @mouseenter="hoverKey = shape.key"
          @mouseleave="hoverKey = null"
          @click="(event: MouseEvent) => select(shape.key, event)"
        >
          <v-icon size="x-small" :icon="icon(shape.tag)" class="text-medium-emphasis" />
          <div class="layer__name">
            <div class="text-body-2">
              {{ shape.tag }}<span v-if="shape.childCount" class="text-medium-emphasis"> · {{ shape.childCount }} formas</span>
            </div>
            <div class="text-caption mono" :class="shape.problem ? 'text-error' : 'text-medium-emphasis'">
              {{ shape.problem ? `⚠ ${shape.problem}` : shape.id ?? 'sem id' }}
            </div>
          </div>
          <v-select
            :model-value="selected(shape)"
            :items="itemsFor(shape)"
            item-title="title"
            item-value="value"
            density="compact"
            variant="outlined"
            hide-details
            class="layer__select"
            placeholder="sem vínculo"
            :disabled="disabled"
            @update:model-value="(value: string | null) => assign(shape.key, value)"
            @click.stop
          />
        </div>
        <div v-if="rows.length === 0" class="pa-4 text-medium-emphasis">
          Nenhuma forma para mostrar.
        </div>
      </div>
    </v-col>
    <v-col cols="12" md="7">
      <sector-map-canvas
        :linking="linking"
        :selected-keys="selectedKeys"
        :hover-key="hoverKey"
        :pick-small="showSmall"
        :show-names="showNames"
        @pick="onPick"
        @hover="key => hoverKey = key"
      />
    </v-col>
  </v-row>
</template>

<script lang="ts" setup>
// The shape linking tool ("Camadas"): the SVG as a layer tree, like a design
// tool's layers panel, with a Sector/Section select per shape. The map follows the tree (hover and
// selection) and the other way round. Ctrl/Shift+click selects several shapes, to group them.
import { computed, mergeProps, nextTick, ref, type ComputedRef, type Ref } from 'vue'
import type { Issue, SectorMapLinking, Shape } from '@/helpers/sectorMap/linking'
import SectorMapCanvas from './sector-map-canvas.vue'

interface Toggle {
  name: string
  icon: string
  model: Ref<boolean>
}

interface Edit {
  name: string
  icon: string
  /** Why the tool can't be used on the selection, shown as its tooltip */
  blocker: string | null
  run: () => void
}

const { linking, disabled = false, fileName = 'mapa-de-setores.svg' } = defineProps<{
  linking: SectorMapLinking
  disabled?: boolean
  fileName?: string
}>()

const selectedKeys = ref<string[]>([])
const hoverKey = ref<string | null>(null)
const onlyProblems = ref(false)
const showSmall = ref(false)
const showNames = ref(true)
const problemsOpen = ref(false)
const historyOpen = ref(false)

const toggles: ComputedRef<Toggle[]> = computed(() => [
  { name: 'Só problemas', icon: 'fa fa-filter', model: onlyProblems },
  { name: `Formas pequenas (${linking.smallCount.value})`, icon: 'fa fa-shapes', model: showSmall },
  { name: 'Nomes no mapa', icon: 'fa fa-tag', model: showNames },
])

// The group's link goes with it, so say which
const ungroupName: ComputedRef<string> = computed(() => {
  const group = linking.shapes.value.find(shape => shape.key === selectedKeys.value[0])
  return group?.target ? `Desfazer grupo (perde o vínculo com ${group.target.label})` : 'Desfazer grupo'
})

const edits: ComputedRef<Edit[]> = computed(() => [
  {
    name: 'Agrupar seleção',
    icon: 'fa fa-object-group',
    blocker: linking.groupBlocker(selectedKeys.value),
    run: () => {
      const key = linking.group(selectedKeys.value)
      if (key) selectedKeys.value = [ key ]
    },
  },
  {
    name: ungroupName.value,
    icon: 'fa fa-object-ungroup',
    blocker: linking.ungroupBlocker(selectedKeys.value),
    run: () => {
      selectedKeys.value = linking.ungroup(selectedKeys.value[0])
    },
  },
])

const levelIcon: Record<Issue['level'], string> = {
  error: 'fa fa-circle-exclamation',
  warning: 'fa fa-triangle-exclamation',
  info: 'fa fa-circle-info',
}

const levelOrder: Issue['level'][] = ['error', 'warning', 'info']

const sortedIssues: ComputedRef<Issue[]> = computed(() => [ ...linking.issues.value ]
  .sort((a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level)))
const errors: ComputedRef<Issue[]> = computed(() => linking.issues.value.filter(i => i.level === 'error'))
const warnings: ComputedRef<Issue[]> = computed(() => linking.issues.value.filter(i => i.level === 'warning'))

const history: ComputedRef<string[]> = computed(() => [ ...linking.history.value ].reverse())

const rows: ComputedRef<Shape[]> = computed(() => linking.shapes.value.filter((shape) => {
  if (shape.small && !showSmall.value) return false
  if (onlyProblems.value) return shape.problem !== null
  return true
}))

interface Option {
  title: string
  value: string | null
  props?: { disabled: boolean }
}

const options: ComputedRef<Option[]> = computed(() => [
  { title: '— sem vínculo —', value: null },
  ...linking.targets.value.map(target => ({
    title: target.kind === 'sector' ? `${target.name} (setor)` : target.label,
    value: target.id,
  })),
])

// A shape whose id is a problem has no target, but must not show as unlinked: picking
// "— sem vínculo —" would then leave the select unchanged and never remove the id.
function selected(shape: Shape) {
  return shape.problem ? shape.id : shape.target?.id ?? null
}

function itemsFor(shape: Shape): Option[] {
  if (!shape.problem) return options.value
  return [...options.value, { title: '⚠ vínculo inválido', value: shape.id, props: { disabled: true } } ]
}

function icon(tag: string) {
  if (tag === 'g') return 'fa fa-folder'
  if (tag === 'polygon') return 'fa fa-draw-polygon'
  return 'fa fa-bezier-curve'
}

function assign(key: string, targetId: string | null) {
  if (targetId === null) {
    linking.unlink(key)
  } else {
    linking.link(key, targetId)
  }
}

function select(key: string, event?: MouseEvent) {
  if (event && (event.ctrlKey || event.metaKey || event.shiftKey)) {
    selectedKeys.value = selectedKeys.value.includes(key)
      ? selectedKeys.value.filter(k => k !== key)
      : [...selectedKeys.value, key]
    return
  }

  selectedKeys.value = [ key ]
}

/** Selects a shape picked on the map or in the Problemas panel, turning off a filter that hides its row. */
async function onPick(key: string, event?: MouseEvent) {
  const shape = linking.shapes.value.find(s => s.key === key)
  if (shape?.small) showSmall.value = true
  if (shape && !shape.problem) onlyProblems.value = false

  select(key, event)
  await nextTick()
  document.getElementById(`layer-${key}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
}

function download() {
  const url = URL.createObjectURL(new Blob([ linking.exportSvg() ], { type: 'image/svg+xml' }))
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  setTimeout(() => URL.revokeObjectURL(url))
}
</script>

<style scoped>
.toolbar {
  background: #fafafa;
}

.mono-wrap {
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
}

.layers {
  max-height: 72vh;
  overflow-y: auto;
}

.layer {
  min-height: 52px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.layer--hover {
  background: #e3f2fd;
}

.layer--selected {
  background: #bbdefb;
}

.layer__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.layer__select {
  max-width: 220px;
  min-width: 220px;
}

.mono {
  font-family: ui-monospace, Menlo, monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
